import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { ApiError } from '@/api/client';
import { apiErrorText } from '@/api/opengater/errors';
import { useOgAuth } from '@/api/opengater/OgAuthContext';
import { useLegalLinks, useUpdateLegalLinks } from '@/api/opengater/useLegalLinks';
import { Icon } from '@/components/Icon';
import { PageTitle } from '@/components/PageTitle';
import { Input } from '@/components/primitives/Input';
import { SegmentedControl } from '@/components/primitives/SegmentedControl';
import { SaveBar, SettingsField, SettingsGroup, SettingsLoading, SettingsNote, } from '@/components/settings/Settings';
import s from './OpengaterLegalLinks.module.css';
const CAP = 'legal-links.write';
const LOCALES = [
    { locale: 'en', label: 'English', title: 'Английский', fallback: true },
    { locale: 'ru', label: 'Русский', title: 'Русский' },
    { locale: 'hy', label: 'Հայերեն', title: 'Армянский' },
    { locale: 'tr', label: 'Türkçe', title: 'Турецкий' },
];
function rowInput(row) {
    return {
        privacy_policy_url: row?.privacy_policy_url ?? null,
        terms_url: row?.terms_url ?? null,
    };
}
function inputValue(value) {
    return value ?? '';
}
function requestValue(value) {
    const trimmed = value?.trim() ?? '';
    return trimmed || null;
}
function statusOf(input) {
    const count = Number(Boolean(requestValue(input.privacy_policy_url))) +
        Number(Boolean(requestValue(input.terms_url)));
    return count === 2 ? 'filled' : count === 1 ? 'partial' : 'empty';
}
const STATUS_TEXT = {
    filled: 'Заполнено',
    partial: 'Частично',
    empty: 'Не заполнено',
};
function validateUrl(value) {
    if (!value?.trim())
        return null;
    if (value.length > 500)
        return 'Ссылка не должна быть длиннее 500 символов.';
    if (!value.startsWith('https://')) {
        return 'Ссылка должна начинаться с https:// и содержать адрес сайта.';
    }
    const hasControlCharacter = [...value].some((character) => {
        const code = character.charCodeAt(0);
        return code <= 31 || code === 127;
    });
    if (/\s/u.test(value) || hasControlCharacter) {
        return 'В ссылке не должно быть пробелов и управляющих символов.';
    }
    if (value.includes(')') || value.includes('\\')) {
        return 'В ссылке не должно быть символов ) и \\.';
    }
    try {
        const parsed = new URL(value);
        if (parsed.protocol !== 'https:' || !parsed.hostname)
            throw new Error('invalid URL');
    }
    catch {
        return 'Ссылка должна начинаться с https:// и содержать адрес сайта.';
    }
    return null;
}
function validationOf(input) {
    const errors = {};
    const privacy = validateUrl(input.privacy_policy_url);
    const terms = validateUrl(input.terms_url);
    if (privacy)
        errors.privacy_policy_url = privacy;
    if (terms)
        errors.terms_url = terms;
    return errors;
}
function sameInput(left, right) {
    return (inputValue(left.privacy_policy_url) === inputValue(right.privacy_policy_url) &&
        inputValue(left.terms_url) === inputValue(right.terms_url));
}
function updatedAt(value) {
    if (!value)
        return 'Ещё не изменялось';
    const date = new Date(value);
    if (Number.isNaN(date.getTime()))
        return 'Дата изменения неизвестна';
    return `Последнее изменение: ${date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })}`;
}
function serverValidation(failure) {
    const empty = { fields: {}, general: [] };
    if (!(failure instanceof ApiError) || failure.status !== 422)
        return empty;
    const detail = failure.body?.detail;
    if (!Array.isArray(detail))
        return empty;
    const result = { fields: {}, general: [] };
    for (const issue of detail) {
        if (!issue || typeof issue !== 'object')
            continue;
        const { loc, msg } = issue;
        if (typeof msg !== 'string')
            continue;
        if (!Array.isArray(loc)) {
            result.general.push(msg);
            continue;
        }
        const field = loc.at(-1);
        if (field === 'privacy_policy_url' || field === 'terms_url') {
            result.fields[field] = msg.replace(/^Value error,\s*/u, '');
        }
        else {
            result.general.push(msg);
        }
    }
    return result;
}
function TabLabel({ label, fallback, status, }) {
    return (_jsxs("span", { className: s.tabLabel, children: [_jsxs("span", { className: s.tabName, children: [label, fallback && _jsx("small", { className: s.fallback, children: "\u0417\u0430\u043F\u0430\u0441\u043D\u043E\u0439" })] }), _jsxs("small", { className: s.tabStatus, "data-status": status, children: [_jsx("span", { className: s.statusDot }), STATUS_TEXT[status]] })] }));
}
export function OpengaterLegalLinks() {
    const { allowed, isDenied, markDenied } = useOgAuth();
    const canManage = allowed(CAP, 'admin');
    const catalog = useLegalLinks(canManage);
    const update = useUpdateLegalLinks();
    const [active, setActive] = useState('en');
    const [drafts, setDrafts] = useState({});
    const [fieldErrors, setFieldErrors] = useState({});
    const [generalError, setGeneralError] = useState(null);
    const rows = useMemo(() => catalog.data ?? [], [catalog.data]);
    const row = rows.find((item) => item.locale === active);
    const original = rowInput(row);
    const draft = drafts[active] ?? original;
    const localErrors = validationOf(draft);
    const errors = { ...localErrors, ...fieldErrors };
    const dirty = !sameInput(draft, original);
    const hasErrors = Object.keys(errors).length > 0;
    useEffect(() => {
        if (catalog.error instanceof ApiError && catalog.error.status === 403)
            markDenied(CAP);
    }, [catalog.error, markDenied]);
    const tabOptions = useMemo(() => LOCALES.map((meta) => {
        const current = drafts[meta.locale] ?? rowInput(rows.find((item) => item.locale === meta.locale));
        return {
            value: meta.locale,
            label: (_jsx(TabLabel, { label: meta.label, fallback: meta.fallback, status: statusOf(current) })),
        };
    }), [drafts, rows]);
    const changeTab = (locale) => {
        if (update.isPending)
            return;
        setActive(locale);
        setFieldErrors({});
        setGeneralError(null);
        update.reset();
    };
    const changeField = (field, value) => {
        if (update.isPending)
            return;
        setDrafts((current) => ({
            ...current,
            [active]: {
                ...(current[active] ?? original),
                [field]: value,
            },
        }));
        setFieldErrors((current) => {
            if (!current[field])
                return current;
            const next = { ...current };
            delete next[field];
            return next;
        });
        setGeneralError(null);
        update.reset();
    };
    const reset = () => {
        setDrafts((current) => {
            const next = { ...current };
            delete next[active];
            return next;
        });
        setFieldErrors({});
        setGeneralError(null);
        update.reset();
    };
    const save = async (event) => {
        event.preventDefault();
        const validation = validationOf(draft);
        setFieldErrors(validation);
        setGeneralError(null);
        if (!dirty || Object.keys(validation).length > 0 || update.isPending)
            return;
        const locale = active;
        const submittedDraft = { ...draft };
        try {
            await update.mutateAsync({
                locale,
                body: {
                    privacy_policy_url: requestValue(draft.privacy_policy_url),
                    terms_url: requestValue(draft.terms_url),
                },
            });
            setDrafts((current) => {
                const currentDraft = current[locale];
                if (currentDraft && !sameInput(currentDraft, submittedDraft))
                    return current;
                const next = { ...current };
                delete next[locale];
                return next;
            });
            setFieldErrors({});
        }
        catch (failure) {
            const validation = serverValidation(failure);
            const hasFieldErrors = Object.keys(validation.fields).length > 0;
            if (hasFieldErrors)
                setFieldErrors(validation.fields);
            if (validation.general.length > 0)
                setGeneralError(validation.general.join('; '));
            if (failure instanceof ApiError && failure.status === 403) {
                setGeneralError('Недостаточно прав.');
                markDenied(CAP);
            }
            else if (!hasFieldErrors && validation.general.length === 0) {
                setGeneralError(apiErrorText(failure, 'Не удалось сохранить ссылки.'));
            }
        }
    };
    const meta = LOCALES.find((item) => item.locale === active);
    return (_jsxs(_Fragment, { children: [_jsx(PageTitle, { title: "\u042E\u0440\u0438\u0434\u0438\u0447\u0435\u0441\u043A\u0438\u0435 \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442\u044B", sub: "\u0421\u0441\u044B\u043B\u043A\u0438 \u0434\u043B\u044F Telegram-\u0431\u043E\u0442\u0430 \u0438 \u0441\u0430\u0439\u0442\u0430" }), !canManage && (_jsx(SettingsNote, { tone: "lock", children: isDenied(CAP)
                    ? 'Недостаточно прав. Раздел доступен только администраторам.'
                    : 'Раздел доступен только администраторам.' })), canManage && (_jsxs("div", { className: s.content, children: [_jsxs("section", { className: s.guide, "aria-label": "\u041A\u0430\u043A \u0438\u0441\u043F\u043E\u043B\u044C\u0437\u0443\u044E\u0442\u0441\u044F \u0441\u0441\u044B\u043B\u043A\u0438", children: [_jsx(Icon, { name: "info", size: 17 }), _jsxs("div", { children: [_jsx("strong", { children: "\u0410\u043D\u0433\u043B\u0438\u0439\u0441\u043A\u0438\u0435 \u0441\u0441\u044B\u043B\u043A\u0438 \u0438\u0441\u043F\u043E\u043B\u044C\u0437\u0443\u044E\u0442\u0441\u044F \u043A\u0430\u043A \u0437\u0430\u043F\u0430\u0441\u043D\u044B\u0435." }), _jsx("span", { children: "\u0415\u0441\u043B\u0438 \u0441\u0441\u044B\u043B\u043A\u0430 \u0434\u043B\u044F \u044F\u0437\u044B\u043A\u0430 \u043D\u0435 \u0437\u0430\u043F\u043E\u043B\u043D\u0435\u043D\u0430, \u0438\u0441\u043F\u043E\u043B\u044C\u0437\u0443\u0435\u0442\u0441\u044F \u0430\u043D\u0433\u043B\u0438\u0439\u0441\u043A\u0430\u044F. \u0411\u0435\u0437 \u0430\u043D\u0433\u043B\u0438\u0439\u0441\u043A\u0438\u0445 \u0441\u0441\u044B\u043B\u043E\u043A \u0444\u0440\u0430\u0437\u0430 \u0432 \u0431\u043E\u0442\u0435 \u043D\u0435 \u043F\u043E\u043A\u0430\u0437\u044B\u0432\u0430\u0435\u0442\u0441\u044F \u0434\u043B\u044F \u044F\u0437\u044B\u043A\u043E\u0432, \u0433\u0434\u0435 \u0441\u0432\u043E\u0438\u0445 \u0441\u0441\u044B\u043B\u043E\u043A \u043D\u0435\u0442." })] })] }), catalog.isLoading && _jsx(SettingsLoading, {}), !!catalog.error && !catalog.isLoading && (_jsx(SettingsNote, { tone: "error", children: apiErrorText(catalog.error, 'Не удалось загрузить юридические ссылки.') })), !catalog.isLoading && !catalog.error && (_jsxs("form", { className: s.form, onSubmit: save, children: [_jsx("fieldset", { className: s.tabs, disabled: update.isPending, children: _jsx(SegmentedControl, { value: active, onChange: changeTab, options: tabOptions }) }), _jsxs(SettingsGroup, { title: meta.title, aside: _jsxs("span", { className: s.localeCode, children: [active.toUpperCase(), meta.fallback && _jsx("span", { children: "\u0437\u0430\u043F\u0430\u0441\u043D\u043E\u0439 \u044F\u0437\u044B\u043A" })] }), footer: updatedAt(row?.updated_at ?? null), children: [_jsx(SettingsField, { label: "\u041F\u043E\u043B\u0438\u0442\u0438\u043A\u0430 \u043A\u043E\u043D\u0444\u0438\u0434\u0435\u043D\u0446\u0438\u0430\u043B\u044C\u043D\u043E\u0441\u0442\u0438", hint: "\u041F\u0443\u0431\u043B\u0438\u0447\u043D\u0430\u044F HTTPS-\u0441\u0441\u044B\u043B\u043A\u0430 \u043D\u0430 \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442", error: errors.privacy_policy_url ? (_jsx("span", { id: "legal-privacy-error", role: "alert", children: errors.privacy_policy_url })) : undefined, children: _jsx(Input, { id: "legal-privacy", type: "url", inputMode: "url", maxLength: 500, value: inputValue(draft.privacy_policy_url), disabled: update.isPending, "aria-label": "\u041F\u043E\u043B\u0438\u0442\u0438\u043A\u0430 \u043A\u043E\u043D\u0444\u0438\u0434\u0435\u043D\u0446\u0438\u0430\u043B\u044C\u043D\u043E\u0441\u0442\u0438", "aria-invalid": Boolean(errors.privacy_policy_url), "aria-errormessage": errors.privacy_policy_url ? 'legal-privacy-error' : undefined, placeholder: "https://example.com/privacy", onChange: (event) => changeField('privacy_policy_url', event.target.value) }) }), _jsx(SettingsField, { label: "\u041F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u0435\u043B\u044C\u0441\u043A\u043E\u0435 \u0441\u043E\u0433\u043B\u0430\u0448\u0435\u043D\u0438\u0435", hint: "\u041F\u0443\u0431\u043B\u0438\u0447\u043D\u0430\u044F HTTPS-\u0441\u0441\u044B\u043B\u043A\u0430 \u043D\u0430 \u0434\u043E\u043A\u0443\u043C\u0435\u043D\u0442", error: errors.terms_url ? (_jsx("span", { id: "legal-terms-error", role: "alert", children: errors.terms_url })) : undefined, children: _jsx(Input, { id: "legal-terms", type: "url", inputMode: "url", maxLength: 500, value: inputValue(draft.terms_url), disabled: update.isPending, "aria-label": "\u041F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u0435\u043B\u044C\u0441\u043A\u043E\u0435 \u0441\u043E\u0433\u043B\u0430\u0448\u0435\u043D\u0438\u0435", "aria-invalid": Boolean(errors.terms_url), "aria-errormessage": errors.terms_url ? 'legal-terms-error' : undefined, placeholder: "https://example.com/terms", onChange: (event) => changeField('terms_url', event.target.value) }) })] }), _jsxs("p", { className: s.cacheNote, children: [_jsx(Icon, { name: "info", size: 14 }), "\u0411\u043E\u0442 \u043F\u0440\u0438\u043C\u0435\u043D\u044F\u0435\u0442 \u043D\u043E\u0432\u044B\u0435 \u0441\u0441\u044B\u043B\u043A\u0438 \u0441\u0440\u0430\u0437\u0443. \u041D\u0430 \u0441\u0430\u0439\u0442\u0435 \u043E\u0431\u043D\u043E\u0432\u043B\u0435\u043D\u0438\u0435 \u043C\u043E\u0436\u0435\u0442 \u0437\u0430\u043D\u044F\u0442\u044C \u0434\u043E \u0447\u0430\u0441\u0430 \u0438\u0437-\u0437\u0430 \u043A\u044D\u0448\u0430."] }), _jsx(SaveBar, { dirty: dirty, saving: update.isPending, saved: update.isSuccess, canSave: dirty && !hasErrors && !update.isPending, error: generalError, onReset: reset, text: `Изменены ссылки: ${meta.title.toLowerCase()}` })] }))] }))] }));
}
